import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Auth0Provider } from "@auth0/auth0-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./index.css";
import Radar from "radar-sdk-js";
import "radar-sdk-js/dist/radar.css";
import App from "./App.jsx";
import { ThemeProvider } from "./contexts/ThemeContext";
import { UserProvider } from "./contexts/UserContext";
import { getCurrentBrand } from "./config/brands";
import { initializeFavicons } from "./utils/faviconUtils";
import { initSentry, runSentrySelfTest } from "./utils/sentry";
import {
  captureInitialAuthErrorFromUrl,
  getPersistedAuthErrorRecord,
} from "./utils/authErrors";
import ErrorBoundary from "./components/ErrorBoundary";

// Capture Auth0 ?error= before React renders (survives redirect loops).
captureInitialAuthErrorFromUrl();

// Initialize Sentry before anything else
initSentry();

// Console helpers (no login required for smoke test / auth debug).
window.getAdminAuthError = () => getPersistedAuthErrorRecord();
if (import.meta.env.VITE_SENTRY_DSN) {
  window.runSentrySelfTest = runSentrySelfTest;
}
console.info("[portal-admin] auth build loaded", {
  version: import.meta.env.VITE_APP_VERSION,
  release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
  mode: import.meta.env.MODE,
  sentry: Boolean(import.meta.env.VITE_SENTRY_DSN),
});

Radar.initialize(import.meta.env.VITE_RADAR_API_KEY);

// Set document title from brand config or environment variable
const brand = getCurrentBrand();
const appName = import.meta.env.VITE_APP_NAME || brand.title || brand.name;
document.title = appName;

// Ensure title stays consistent (prevent any components from changing it)
const originalTitle = appName;
setInterval(() => {
  if (document.title !== originalTitle) {
    document.title = originalTitle;
  }
}, 100);

// Initialize favicons on app load
initializeFavicons();

const domain = import.meta.env.VITE_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 min - data considered fresh, no refetch
      gcTime: 10 * 60 * 1000, // 10 min - keep unused data in cache
    },
  },
});

createRoot(document.getElementById("root")).render(
  <StrictMode>
    <ErrorBoundary>
      <ThemeProvider>
        <Auth0Provider
          domain={domain}
          clientId={clientId}
          authorizationParams={{
            redirect_uri: window.location.origin,
            audience: audience, // Required for API tokens - must match API's AUTH0_AUDIENCE
            scope: 'openid profile email', // Explicitly request email scope
          }}
          cacheLocation="localstorage" // Cache tokens in localStorage for better performance
          useRefreshTokens={true} // Enable refresh tokens to avoid re-authentication
        >
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <UserProvider>
                <App />
              </UserProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </Auth0Provider>
      </ThemeProvider>
    </ErrorBoundary>
  </StrictMode>
);
